import React, { useEffect, useState } from "react";
import DashboardHeader from "../../components/auth/DashboardHeader";
import Sidebar from "../../components/auth/Sidebar";
import { Loader } from "../../components/Loader";
import API from "../../helper/AxiosInterceptor";
import { toast } from "react-toastify";
import copy from "copy-to-clipboard";
import { FaCopy, FaEye, FaEyeSlash } from "react-icons/fa";
import { useAppSelection } from "../../context/AppSelectionContext";

const WebhookSettings = () => {
    const { selectedApp, setSelectedApp, apps, fetchApps } = useAppSelection();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState("");
    const [webhookSecret, setWebhookSecret] = useState("");
    const [showSecret, setShowSecret] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [secretKey, setSecretKey] = useState("");

    // Load webhook settings from selected app
    const loadAppWebhookSettings = async (app) => {
        setWebhookUrl(app.webhookUrl || "");
        // Fetch API keys for this app
        await fetchApiKeys(app._id);
    };

    // Fetch API keys
    const fetchApiKeys = async (appId) => {
        try {
            const response = await API.get(
                `${process.env.REACT_APP_API_URL}/apps/keys`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params: {
                        appId: appId,
                    },
                }
            );

            if (response?.data) {
                setApiKey(response?.data?.publicKey);
                setSecretKey(response?.data?.privateKey);
            }
        } catch (error) {
            console.error("Error fetching API keys:", error);
        }
    };

    useEffect(() => {
        fetchApps();
    }, []);

    // Load settings when selectedApp changes
    useEffect(() => {
        if (selectedApp && apps.length > 0) {
            const app = apps.find((a) => a._id === selectedApp);
            if (app) {
                loadAppWebhookSettings(app);
            }
        }
    }, [selectedApp, apps]);

    const handleAppChange = async (e) => {
        const appId = e.target.value;
        setSelectedApp(appId);
    };

    const handleSave = async () => {
        if (!selectedApp) {
            toast.error("Please select an app");
            return;
        }

        if (!webhookUrl) {
            toast.error("Webhook URL is required");
            return;
        }

        // Validate URL format
        try {
            new URL(webhookUrl);
        } catch (e) {
            toast.error("Please enter a valid URL");
            return;
        }

        try {
            setIsSaving(true);
            await API.post(
                `${process.env.REACT_APP_API_URL}/apps/webhook/update`,
                {
                    appId: selectedApp,
                    apiKey: apiKey,
                    secretKey: secretKey,
                    webhookUrl: webhookUrl,
                    webhookSecret: webhookSecret || undefined,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
            toast.success("Webhook settings saved successfully!");

            // Refresh app data
            await fetchApps();
        } catch (error) {
            console.error("Error saving webhook settings:", error);
            toast.error(error?.response?.data?.message || "Failed to save webhook settings");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <DashboardHeader />

            <div className="site-bg">
                <div className="dashborad-content-bg">
                    <aside className="sidebar">
                        <Sidebar />
                    </aside>
                    <div className="dashborad-warper" style={{ position: "relative" }}>
                        {isLoading && <Loader inline={true} />}
                        <div className="transactions-list">
                            <div className="dashborad-head">
                                <div className="breadcrumb-menu">
                                    <h3>Webhook Settings</h3>
                                    <p>Configure webhook URL to receive instant payment and withdrawal notifications</p>
                                </div>
                            </div>

                            <div className="white-bg my-apps-content padd20">
                                {/* App Selector */}
                                <div className="form-group" style={{ marginBottom: "30px" }}>
                                    <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                        Select Application
                                    </label>
                                    <select
                                        className="form-control"
                                        value={selectedApp}
                                        onChange={handleAppChange}
                                        style={{ maxWidth: "400px" }}
                                    >
                                        <option value="">Select App</option>
                                        {apps.map((app) => (
                                            <option key={app._id} value={app._id}>
                                                {app.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedApp && (
                                    <>
                                        {/* Info Box */}
                                        <div style={{
                                            backgroundColor: "#f0f8ff",
                                            border: "1px solid #b3d9ff",
                                            borderRadius: "8px",
                                            padding: "20px",
                                            marginBottom: "30px"
                                        }}>
                                            <h4 style={{ color: "#0066cc", marginBottom: "10px", fontSize: "16px" }}>
                                                ℹ️ What are Webhooks?
                                            </h4>
                                            <p style={{ color: "#333", fontSize: "14px", marginBottom: "10px" }}>
                                                Webhooks allow you to receive instant notifications when events occur in your PayCoinz account.
                                            </p>
                                            <p style={{ color: "#333", fontSize: "14px", marginBottom: "0" }}>
                                                <strong>Events you'll receive:</strong>
                                            </p>
                                            <ul style={{ color: "#555", fontSize: "13px", marginTop: "8px", paddingLeft: "20px" }}>
                                                <li>Payment initiated, confirmed, success, expired</li>
                                                <li>Withdrawal pending, approved, declined, processing, success, failed</li>
                                            </ul>
                                        </div>

                                        {/* Webhook URL */}
                                        <div style={{ marginBottom: "30px" }}>
                                            <h4 style={{ marginBottom: "15px", color: "#333" }}>Webhook Configuration</h4>

                                            <div className="form-group" style={{ marginBottom: "20px" }}>
                                                <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                    Webhook URL *
                                                </label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    placeholder="https://your-domain.com/webhook"
                                                    value={webhookUrl}
                                                    onChange={(e) => setWebhookUrl(e.target.value)}
                                                    style={{ maxWidth: "600px" }}
                                                />
                                                <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
                                                    The URL where you want to receive webhook notifications
                                                </small>
                                            </div>

                                            <div className="form-group" style={{ marginBottom: "20px" }}>
                                                <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                    Webhook Secret (Optional)
                                                </label>
                                                <div style={{ display: "flex", alignItems: "center", gap: "10px", maxWidth: "600px" }}>
                                                    <input
                                                        type={showSecret ? "text" : "password"}
                                                        className="form-control"
                                                        placeholder="Enter a secret key for HMAC signature verification"
                                                        value={webhookSecret}
                                                        onChange={(e) => setWebhookSecret(e.target.value)}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowSecret(!showSecret)}
                                                        style={{
                                                            background: "none",
                                                            border: "1px solid #ddd",
                                                            padding: "8px 12px",
                                                            borderRadius: "4px",
                                                            cursor: "pointer",
                                                            color: "#666"
                                                        }}
                                                    >
                                                        {showSecret ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>
                                                <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
                                                    If not provided, your API Secret Key will be used for signing webhooks
                                                </small>
                                            </div>
                                        </div>

                                        {/* Webhook Testing */}
                                        <div style={{
                                            backgroundColor: "#fff9e6",
                                            border: "1px solid #ffe066",
                                            borderRadius: "8px",
                                            padding: "20px",
                                            marginBottom: "30px"
                                        }}>
                                            <h4 style={{ color: "#cc8800", marginBottom: "10px", fontSize: "16px" }}>
                                                🧪 Testing Webhooks
                                            </h4>
                                            <p style={{ color: "#333", fontSize: "14px", marginBottom: "10px" }}>
                                                To test your webhook integration, you can use:
                                            </p>
                                            <ul style={{ color: "#555", fontSize: "13px", marginTop: "8px", paddingLeft: "20px", marginBottom: "10px" }}>
                                                <li><strong>webhook.site</strong> - Get a free test URL and see webhook payloads in real-time</li>
                                                <li><strong>Postman Echo</strong> - Use <code>https://postman-echo.com/post</code> for quick testing</li>
                                            </ul>
                                            <p style={{ color: "#555", fontSize: "13px", marginBottom: "0" }}>
                                                After saving, trigger a withdrawal or payment to see the webhook in action!
                                            </p>
                                        </div>

                                        {/* Webhook Signature Info */}
                                        <div style={{ marginBottom: "30px" }}>
                                            <h4 style={{ marginBottom: "15px", color: "#333" }}>Webhook Signature Verification</h4>
                                            <p style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>
                                                All webhooks are signed with HMAC-SHA256. You'll receive the signature in the <code>X-Webhook-Signature</code> header.
                                            </p>

                                            <div style={{ backgroundColor: "#f5f5f5", padding: "15px", borderRadius: "6px", marginBottom: "15px" }}>
                                                <p style={{ color: "#333", fontSize: "13px", marginBottom: "8px", fontWeight: "600" }}>
                                                    Example verification (Node.js):
                                                </p>
                                                <pre style={{
                                                    backgroundColor: "#2d2d2d",
                                                    color: "#f8f8f2",
                                                    padding: "15px",
                                                    borderRadius: "4px",
                                                    fontSize: "12px",
                                                    overflowX: "auto",
                                                    margin: 0
                                                }}>
                                                    {`const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const expectedSignature = 'sha256=' + 
    crypto.createHmac('sha256', secret)
      .update(JSON.stringify(payload))
      .digest('hex');
  
  return signature === expectedSignature;
}`}
                                                </pre>
                                            </div>
                                        </div>

                                        {/* Save Button */}
                                        <div style={{ marginTop: "20px" }}>
                                            <button
                                                type="button"
                                                onClick={handleSave}
                                                disabled={isSaving}
                                                style={{
                                                    backgroundColor: "#f5a623",
                                                    color: "#fff",
                                                    border: "none",
                                                    padding: "12px 30px",
                                                    borderRadius: "8px",
                                                    fontSize: "16px",
                                                    fontWeight: "600",
                                                    cursor: isSaving ? "not-allowed" : "pointer",
                                                    opacity: isSaving ? 0.7 : 1,
                                                }}
                                            >
                                                {isSaving ? "Saving..." : "Save Webhook Settings"}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WebhookSettings;
